import LocalStorage from "@/classes/local-storage";
import { getNewNoteTemplate } from "@/data/new-note-template";
import { globals } from "@/globals";
import {
	BookmarkInfo,
	CornerMessageInfo,
	DialogInfo,
	DynamicInfo,
	EditShortcutPopupInfo,
	FloatNotificationInfo,
	FolderInfo,
	GeneralSettingsInfo,
	LoginInfo,
	MenuInfo,
	PromptInfo,
	SearchEngineInfo,
	TnInfo,
} from "@/types";
import { signal } from "@preact/signals";
import { t } from "i18next";

const initialGeneralSettings: GeneralSettingsInfo = {
	bingWallpaperResolution:
		screen.width * devicePixelRatio > 1920 ||
		screen.height * devicePixelRatio > 1920
			? "uhd"
			: "fhd",
	currentWallpaper: "Default3",
	is24HourTime: true,
	isAirportalCoordinationEnabled: false,
	isBackdropBlurEnabled: true,
	isBackdropBlurEnabledForShortcuts: false,
	isDockEnabled: true,
	isFooterEnabled:
		!globals.isIframe &&
		(globals.isChinaTimeZone || navigator.language.endsWith("-CN")),
	isLiteModeEnabled: false,
	isQuoteEnabled: true,
	isSearchHistoryEnabled: true,
	language: undefined,
	searchBarTabKeyAction: "nextSearchEngineInList",
	shouldAddVignettingToWallpaper: true,
	shouldAutoClearSearchBar: true,
	shouldFocusOnSearchBarOnLoad: !globals.isIframe,
	shouldOpenInNewPage: !globals.isExtension && !globals.isFirefox,
	shouldReduceMotion: window.matchMedia("(prefers-reduced-motion)").matches,
	shouldShowGreeting: true,
	shouldShowMoreShortcutsPerRow: false,
	shouldShowPinnedNoteCompleteContent: true,
	shouldShowSecondaryScreenOnLoad: false,
	shouldShowSeconds: false,
	shouldShrinkSearchBar: false,
	shouldTimeTwinkle: false,
	theme: "system",
	themeColor: "lime",
	timeFontWeight: "bold",
	...LocalStorage.getGeneralSettings(),
};

export const login = signal<LoginInfo>({});

export const arePopupsShown = signal({
	about: false,
	aiTools: false,
	donate: false,
	feedback: false,
	generalSettings: false,
	login: false,
	migration: false,
	notes: false,
	searchEngine: false,
	wallpaper: false,
	welcome: globals.isMajorUpdate,
});
export const cornerMessage = signal<CornerMessageInfo>({});
export const currentFolder = signal<FolderInfo | null>(null);
export const currentNote = signal(getNewNoteTemplate());
export const dialog = signal<DialogInfo | null>(null);
export const dynamicInfo = signal<DynamicInfo>({});
export const editingFolder = signal<FolderInfo>({
	children: [],
	folderName: "",
	id: "",
	itemType: "folder",
});
export const editingShortcut = signal<Omit<BookmarkInfo, "onClick">>({
	icon: "",
	itemType: "bookmark",
	title: "",
	url: "",
});
export const editShortcutPopup = signal<EditShortcutPopupInfo>({
	isShown: false,
	position: { left: 0, top: 0 },
});
export const errorMessage = signal<string | null>(null);
export const floatNotification = signal<FloatNotificationInfo>({});
export const generalSettings = signal(initialGeneralSettings);
export const greetingText = signal("");
export const isClosingMenu = signal(false);
export const isClosingSearchBar = signal(false);
export const isClosingSearchSuggestions = signal(false);
export const isClosingSecondaryScreen = signal(false);
export const isDark = signal<boolean | undefined>(undefined);
export const isDragging = signal(false);
export const isLoadingShown = signal(false);
export const isSearchFocused = signal(false);
export const isSearchSuggestionsShown = signal(false);
export const isSecondaryScreenShown = signal(
	initialGeneralSettings.shouldShowSecondaryScreenOnLoad,
);
export const isWallpaperFocused = signal(
	initialGeneralSettings.shouldShowSecondaryScreenOnLoad,
);
export const liveWallpaperPrefix = signal<string | null>(null);
export const menu = signal<MenuInfo | null>(null);
export const prompt = signal<PromptInfo>({});
export const renameShortcutFolderPopup = signal<EditShortcutPopupInfo>({
	isShown: false,
	position: { left: 0, top: 0 },
});
export const searchHistory = signal<string[]>(LocalStorage.getSearchHistory());
export const searchInputValue = signal("");
export const selectedSearchEngine = signal<SearchEngineInfo>({
	icon: "\uE651",
	id: "preset-baidu",
	name: t("baidu"),
	url: "https://www.baidu.com/s?ie=utf-8&word=",
});
export const tnInfo = signal<TnInfo>({
	ch: localStorage.getItem("ch") || "",
	tn: localStorage.getItem("tn") || "",
});
export const wallpaper = signal<{
	fallbackUrl?: string;
	isVideo?: boolean;
	url?: string;
}>({});
